import {getLocaleText} from '../../ts/util/translation';
import chevronImageURL from '../../../../public/img/chevron-down-solid.svg';
import {maybeFetchWP} from '../../../@shared/ts/maybe';
import {FeatureFlag} from '../../../@type/features';
import {Feature, startModalLoading, stopModalLoading} from '../reducers/environmentReducer';
import {getErrorString} from '../../../@shared/ts/error';
import {store} from '../store';
import {requestCartCalculation} from '../payment/order';
import {$qs} from '../../../@shared/ts/dom';
import {type CheckoutData} from '../models/CheckoutData';
import {Carts} from '../reducers/cartReducer';

export function initMerchantAccount(checkoutData: CheckoutData) {
	const accountDetails = checkoutData.merchant_customer_account;

	// If the user is logged in then we don't need to do anything
	if (accountDetails.logged_in) {
		return;
	}

	if (accountDetails.checkout_login_enabled) {
		mountLoginHTML();
		setupLoginEvents();
	}

	if (accountDetails.checkout_registration_enabled || accountDetails.checkout_registration_with_subscription_enabled) {
		mountRegistrationHTML(accountDetails);
		setupRegistrationEvents();
	}

	let previousCartContainsSubscription = false;
	const unsubscribe = store.subscribe(() => {
		// A user might get logged in after placing the order but then encountering an error with the
		// payment. In this case we need to remove the login/registration forms.
		const isLoggedIn = Feature.dynamicMetadata<boolean>(FeatureFlag.EXPRESS_CHECKOUT, 'logged_in') ?? false;
		if (isLoggedIn) {
			document.querySelector('#login-container')?.remove();
			document.querySelector('#register-container')?.remove();

			unsubscribe();
		}

		const currentCartContainsSubscription = Carts.subscriptionPresent();
		if (previousCartContainsSubscription === currentCartContainsSubscription) {
			return;
		}

		previousCartContainsSubscription = currentCartContainsSubscription;

		// At this point we can assume:
		// 1. The user is not logged in
		// 2. The user has the option to register an account

		// At this point we cannot assume:
		// 1. The user may or may not have the option to login
		// 2. The user may or may not have a subscription in their cart

		toggleRegistrationDisplay(shouldShowRegistration(accountDetails, Carts.subscriptionPresent()));
		toggleRegistrationCheckboxDisplay(shouldShowRegistrationCheckbox(accountDetails, Carts.subscriptionPresent()));
		toggleRegistrationCredentialsDisplay(shouldShowRegistrationCredentials(accountDetails, Carts.subscriptionPresent()));
	});
}

function mountLoginHTML() {
	const lostPasswordURL = Feature.metadata<string>(FeatureFlag.EXPRESS_CHECKOUT, 'lost_password_url') ?? '';

	const html = `
	<div id="login-container">
		<style>
			#login-container details {
				border-radius: 8px;
				border: 1px solid #e0e0e0;
				margin-bottom: 12px;
				overflow: hidden;
			}
			#login-container summary {
				padding: 8px;
				border-radius: 8px;
				cursor: pointer;
				display: flex;
				flex-direction: row;
				align-items: center;
				background-color: var(--peachpay-theme-color-light);

			}
			#login-container details[open] summary {
				border-bottom-left-radius: 0;
				border-bottom-right-radius: 0;
				border-bottom: 1px solid #e0e0e0;
			}
			#login-container details[open] summary > img {
				transform: rotate(180deg);
			}
			#login-container form {
				padding: 8px;
			}
			details > summary {
				list-style: none;
			}
			details > summary::-webkit-details-marker {
				display: none;
			}

			.forgot-password{
				border-radius: 8px;
				padding: 8px;
				color: var(--peachpay-theme-color);
			}

			.forgot-password:hover{
				background-color: var(--peachpay-theme-color-light);
			}
		</style>
		<details>
			<summary>
				<span style="flex: 1;">${getLocaleText('Have an account?')}</span>
				<img src="${chevronImageURL}" class="pp-accordion-arrow" style="scale: 1.2; margin-right: 12px">
			</summary>
			<span style="display: block; margin: 8px 8px 0;">
				${getLocaleText('If you have shopped with us before, please enter your login details below. If you are a new customer, please proceed to the next section.')}
			</span>
			<form id="login-user" class="pp-form" style="margin-top: 8px 8px 0x;">
				<div class="pp-fw-50 flex">
					<input name="username" type="text" class="pp-fw-100 text-input" placeholder=" " required/>
					<label class="pp-form-label" for="username">${getLocaleText('Username or email')}</label>
				</div>
				<div class="pp-fw-50 flex">
					<input name="password" type="password" class="pp-fw-100 text-input" placeholder=" " required/>
					<label class="pp-form-label" for="password">${getLocaleText('Password')}</label>
				</div>
				<span id="login-error" class="error hide"></span>
				<label class="pp-fw-100 flex">
					<input name="remember" type="checkbox" value="forever"/>
					<span>${getLocaleText('Remember me')}</span>
				</label>
			</form>
			<div class="flex row" style="padding: 8px;">
				<button type="submit" form="login-user" class="btn" style="padding: 8px; min-width: 7rem; width: auto; margin-right: 8px;">${getLocaleText('Login')}</button>
				<a class="forgot-password" href="${lostPasswordURL}" onclick="window.top.location.href = this.href; return false;">${getLocaleText('Lost your password?')}</a>
			</div>
		</details>
	</div>`;

	document.querySelector('#pp-billing-page')?.insertAdjacentHTML('afterbegin', html);
}

function setupLoginEvents() {
	document.querySelector('#login-user')?.addEventListener('submit', async event => {
		event.preventDefault();

		const $form = event.target as HTMLFormElement;
		const ajaxURL = Feature.metadata<string>(FeatureFlag.EXPRESS_CHECKOUT, 'admin_ajax_url');
		const loginNonce = Feature.dynamicMetadata<string>(FeatureFlag.EXPRESS_CHECKOUT, 'login_nonce');

		$qs('#login-error')?.classList.add('hide');

		if (!$form || !ajaxURL || !loginNonce) {
			$qs('#login-error', $el => {
				$el.innerHTML = getLocaleText('An unknown error occurred while logging in. Please refresh the page and try again.');
				$el.classList.remove('hide');
			});
			return;
		}

		store.dispatch(startModalLoading());

		const formData = new FormData($form);
		formData.append('security', loginNonce);
		formData.append('action', 'peachpay_ajax_login');

		const {error: loginError, result: loginResult} = await maybeFetchWP<{success: boolean; message: string}>(ajaxURL, {
			method: 'POST',
			body: formData,
		});

		if (loginError || !loginResult || !loginResult.success) {
			const errorMessage = loginError ? getErrorString(loginError) : loginResult?.message ?? 'An unknown error occurred while logging in. Please try again later.';
			const cleanHTML = (htmlString: string) => {
				const temporalDivElement = document.createElement('div');
				temporalDivElement.innerHTML = htmlString;
				temporalDivElement.querySelectorAll('a,script').forEach($el => {
					$el.remove();
				});
				return temporalDivElement.innerHTML;
			};

			$qs('#login-error', $el => {
				$el.innerHTML = cleanHTML(errorMessage);
				$el.classList.remove('hide');
			});
			store.dispatch(stopModalLoading());
			return;
		}

		// Remove the login/registration forms from the checkout. They are no longer needed.
		document.querySelector('#login-container')?.remove();
		document.querySelector('#register-container')?.remove();

		await requestCartCalculation('pull', 'account:login');
		store.dispatch(stopModalLoading());
	});
}

function mountRegistrationHTML(accountDetails: CheckoutData['merchant_customer_account']) {
	const usernameHTML = (displayed: boolean) => `
		<div id="register-username" class="${accountDetails.auto_generate_password ? 'pp-fw-100' : 'pp-fw-50'} flex">
			<input name="account_username" type="text" class="pp-fw-100 text-input" placeholder=" " required ${displayed ? '' : 'disabled'}/>
			<label class="pp-form-label" for="account_username">${getLocaleText('Account username')}</label>
		</div>`;

	const passwordHTML = (displayed: boolean) => `
		<div id="register-password" class="${accountDetails.auto_generate_username ? 'pp-fw-100' : 'pp-fw-50'} flex">
			<input name="account_password" type="password" class="pp-fw-100 text-input" placeholder=" " required ${displayed ? '' : 'disabled'}/>
			<label class="pp-form-label" for="account_password">${getLocaleText('Create account password')}</label>
		</div>`;

	const showRegistration = shouldShowRegistration(accountDetails, false);
	const showRegistrationCheckbox = shouldShowRegistrationCheckbox(accountDetails, false);
	const showRegistrationCredentials = shouldShowRegistrationCredentials(accountDetails, false);

	const containerHTML = `
	<div id="register-container" class="pp-form pp-fw-100 ${showRegistration ? '' : 'hide'}">
		<div id="register-account" class="flex pp-fw-100 ${showRegistrationCheckbox ? '' : 'hide'}">
			<div class="pp-fw-100 flex">
				<label>
					<input id="createaccount" type="checkbox" name="createaccount" value="1"/>
					${getLocaleText('Create an account?')}
				</label>
			</div>
		</div>
		<div id="register-credentials" class="pp-form pp-fw-100 ${showRegistrationCredentials ? '' : 'hide'}">
			${accountDetails.auto_generate_username ? '' : usernameHTML(showRegistrationCredentials)}
			${accountDetails.auto_generate_password ? '' : passwordHTML(showRegistrationCredentials)}
		</div>
	</div>`;

	document.querySelector('#pp-billing-form')?.insertAdjacentHTML('beforeend', containerHTML);
}

function setupRegistrationEvents() {
	document.querySelector('#createaccount')?.addEventListener('click', async event => {
		const $target = event.target as HTMLInputElement;

		toggleRegistrationCredentialsDisplay($target.checked);
	});
}

function shouldShowRegistrationCheckbox(accountDetails: CheckoutData['merchant_customer_account'], subscriptionPresent: boolean) {
	if (subscriptionPresent) {
		return false;
	}

	return accountDetails.allow_guest_checkout;
}

function toggleRegistrationCheckboxDisplay(show: boolean) {
	document.querySelector('#register-account')?.classList.toggle('hide', !show);
}

function shouldShowRegistrationCredentials(accountDetails: CheckoutData['merchant_customer_account'], subscriptionPresent: boolean) {
	return subscriptionPresent || !accountDetails.allow_guest_checkout;
}

function toggleRegistrationCredentialsDisplay(show: boolean) {
	if (show) {
		$qs('#register-credentials')?.classList.remove('hide');
		$qs('#register-username input')?.removeAttribute('disabled');
		$qs('#register-password input')?.removeAttribute('disabled');
	} else {
		$qs('#register-credentials')?.classList.add('hide');
		// Add disabled so form validation doesn't complain when fields are not displayed
		$qs('#register-username input')?.setAttribute('disabled', '');
		$qs('#register-password input')?.setAttribute('disabled', '');
	}
}

function shouldShowRegistration(accountDetails: CheckoutData['merchant_customer_account'], subscriptionPresent: boolean): boolean {
	// Registration is shown if:
	//     Checkout registration is enabled
	//   or:
	//     A subscription is present and registration with a subscription is enabled
	return accountDetails.checkout_registration_enabled || (subscriptionPresent && accountDetails.checkout_registration_with_subscription_enabled);
}

function toggleRegistrationDisplay(show: boolean) {
	$qs('#register-container')?.classList.toggle('hide', !show);
}
